import { H5 } from '@wheely/ui-kit';
import React from 'react';
import { useIntl } from 'react-intl';

import { IDocUploaderForm } from '@/shared/store/doc-uploader/types';

import { TOnChangeValue } from '../DocUploadBlock';

import { QuestionWithDoc } from './QuestionWithDoc';
import s from './styles.module.css';
import { ISupplementaryDoc } from './supplementary-docs';
import { SupplementaryDocUpload } from './SupplementaryDocUpload';
import { ISupplementaryDocsGroup, IUploadError, TQuestionAnswer } from './types';

type TOnChangeSupplementaryValue<TDocType extends string> = <
  K extends keyof IDocUploaderForm<TDocType>,
>(
  docType: TDocType,
  change: { name: K; value: IDocUploaderForm<TDocType>[K] },
) => void;

interface IProps<TDocType extends string> {
  group: ISupplementaryDocsGroup;
  docs: ISupplementaryDoc<TDocType>[];
  isUploading: boolean;
  uploadError?: IUploadError;
  learnMoreHref?: string;
  onChangeValue: TOnChangeSupplementaryValue<TDocType>;
  onAnswer: (docType: TDocType, answer: TQuestionAnswer) => void;
}

const SupplementaryQuestions = <TDocType extends string>({
  group,
  docs,
  isUploading,
  uploadError,
  learnMoreHref,
  onChangeValue,
  onAnswer,
}: IProps<TDocType>) => {
  const intl = useIntl();
  const askedDocs = docs.filter(doc => doc.question.ask);

  const learnMore = {
    a: (chunks: React.ReactNode[]) => (
      <a href={learnMoreHref} target="_blank" rel="noreferrer">
        {chunks}
      </a>
    ),
  };

  return (
    <div className={s.section} data-test-id="dialog__supplementary-docs">
      {group.sectionTitle && (
        <div className={s.sectionHeader}>
          <H5 capitalize={true}>{intl.formatMessage(group.sectionTitle)}</H5>
        </div>
      )}
      {docs.map(doc => {
        const { docType } = doc.form;
        const { ask } = doc.question;
        const error = uploadError?.docType === docType ? uploadError.message : undefined;
        const handleChangeValue: TOnChangeValue<TDocType> = (_, change) =>
          onChangeValue(docType, change);

        if (!ask) {
          return (
            <SupplementaryDocUpload
              key={docType}
              variant="main"
              doc={doc}
              isUploading={isUploading}
              error={error}
              onChangeValue={handleChangeValue}
            />
          );
        }

        return (
          <QuestionWithDoc
            key={docType}
            index={askedDocs.indexOf(doc) + 1}
            doc={doc}
            question={intl.formatMessage(ask.question)}
            hint={intl.formatMessage(ask.hint, learnMore)}
            isUploading={isUploading}
            error={error}
            onChangeValue={handleChangeValue}
            onAnswer={answer => onAnswer(docType, answer)}
          />
        );
      })}
    </div>
  );
};

export type { TOnChangeSupplementaryValue };
export { SupplementaryQuestions };
